import { Tabs } from "@base-ui/react/tabs";
import { Dropzone } from "./Dropzone";
import { EmptyFiles } from "./EmptyFiles";
import { FileSection } from "./FileSection";
import type { PublicPadFile } from "../shared";

export function FilesPanel({
  files,
  uploading,
  onFiles,
  onDelete,
  onNotice,
}: {
  files: PublicPadFile[];
  uploading: boolean;
  onFiles: (files: FileList | File[]) => void;
  onDelete: (fileId: string) => Promise<void>;
  onNotice: (message: string) => void;
}) {
  const imageFiles = files.filter((file) => file.type.startsWith("image/"));
  const otherFiles = files.filter((file) => !file.type.startsWith("image/"));

  return (
    <aside className="min-h-[520px] min-w-0 overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_rgba(0,0,0,0.03)] lg:min-h-0">
      <Tabs.Root defaultValue="all" className="grid h-full min-h-0 min-w-0 grid-rows-[auto_auto_minmax(0,1fr)]">
        <Tabs.List className="relative flex h-14 items-end gap-5 border-b border-neutral-200 px-4" aria-label="Attachment filters">
          <Tab value="all" label="All" count={files.length} />
          <Tab value="images" label="Images" count={imageFiles.length} />
          <Tab value="files" label="Files" count={otherFiles.length} />
        </Tabs.List>

        <Dropzone uploading={uploading} onFiles={onFiles} />

        <Tabs.Panel value="all" className="min-h-0 overflow-auto p-4 outline-none focus-visible:ring-2 focus-visible:ring-neutral-950 focus-visible:ring-inset">
          {files.length ? (
            <div className="space-y-5">
              <FileSection title="Images" files={imageFiles} onDelete={onDelete} onNotice={onNotice} />
              <FileSection title="Files" files={otherFiles} onDelete={onDelete} onNotice={onNotice} />
            </div>
          ) : (
            <EmptyFiles label="Paste or drop files to make them available on every device with this link." />
          )}
        </Tabs.Panel>

        <Tabs.Panel value="images" className="min-h-0 overflow-auto p-4 outline-none focus-visible:ring-2 focus-visible:ring-neutral-950 focus-visible:ring-inset">
          {imageFiles.length ? (
            <FileSection title="Images" files={imageFiles} onDelete={onDelete} onNotice={onNotice} />
          ) : (
            <EmptyFiles label="Images you paste or upload will appear here." />
          )}
        </Tabs.Panel>

        <Tabs.Panel value="files" className="min-h-0 overflow-auto p-4 outline-none focus-visible:ring-2 focus-visible:ring-neutral-950 focus-visible:ring-inset">
          {otherFiles.length ? (
            <FileSection title="Files" files={otherFiles} onDelete={onDelete} onNotice={onNotice} />
          ) : (
            <EmptyFiles label="Text files and other attachments will appear here." />
          )}
        </Tabs.Panel>
      </Tabs.Root>
    </aside>
  );
}

function Tab({ value, label, count }: { value: string; label: string; count: number }) {
  return (
    <Tabs.Tab
      value={value}
      className="relative h-full border-b-2 border-transparent px-1 text-sm font-medium text-neutral-500 outline-none transition-[border-color,color,box-shadow] hover:text-neutral-950 data-[selected]:border-neutral-950 data-[selected]:text-neutral-950 focus-visible:ring-2 focus-visible:ring-neutral-950 focus-visible:ring-offset-2"
    >
      {label}
      <span className="ml-2 rounded-full bg-neutral-100 px-2 py-0.5 text-xs text-neutral-500 ring-1 ring-inset ring-neutral-200">{count}</span>
    </Tabs.Tab>
  );
}
